import { useCallback } from "react";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
	Textarea,
} from "@/components/primitives";
import { BookOpen } from "lucide-react";
import type { Reflection, ReflectionResponse } from "../types";

interface ReflectionDetailsSectionProps {
	selectedReflectionId: string;
	reflections: Reflection[];
	reflectionResponses: Record<string, ReflectionResponse>;
	setReflectionResponses: (
		value:
			| Record<string, ReflectionResponse>
			| ((
					prev: Record<string, ReflectionResponse>,
			  ) => Record<string, ReflectionResponse>),
	) => void;
}

export function ReflectionDetailsSection({
	selectedReflectionId,
	reflections,
	reflectionResponses,
	setReflectionResponses,
}: ReflectionDetailsSectionProps) {
	const handleSignalToggle = useCallback(
		(reflectionId: string, signalId: string, checked: boolean) => {
			setReflectionResponses((prev) => {
				const prevEntry = prev[reflectionId] || {
					impact: null,
					reasons: "",
					signals: [],
				};
				const signals = new Set(prevEntry.signals || []);
				if (checked) signals.add(signalId);
				else signals.delete(signalId);
				return {
					...prev,
					[reflectionId]: { ...prevEntry, signals: Array.from(signals) },
				};
			});
		},
		[setReflectionResponses],
	);

	const selectedReflection = reflections.find(
		(r) => r.id === selectedReflectionId,
	);

	if (!selectedReflection) {
		return null;
	}

	return (
		<div className="pt-8 border-t">
			<div className="flex items-center gap-3 mb-6">
				<div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
					<BookOpen className="h-5 w-5 text-purple-600 dark:text-purple-400" />
				</div>
				<h3 className="text-xl font-semibold text-foreground">
					Reflection Details
				</h3>
			</div>
			<div className="space-y-6">
				<div className="border-2 rounded-xl p-6 bg-gradient-to-br from-purple-50/50 to-indigo-50/50 dark:from-purple-950/20 dark:to-indigo-950/20 border-purple-200 dark:border-purple-900/30 shadow-sm">
					<h4 className="font-semibold text-lg mb-2 text-foreground">
						{selectedReflection.title}
					</h4>
					<p className="text-sm text-muted-foreground mb-4 leading-relaxed">
						{selectedReflection.description.slice(0, 150)}
						{selectedReflection.description.length > 150 ? "..." : ""}
					</p>

					<div className="space-y-4 pt-2">
						<div>
							<label className="block text-sm font-semibold mb-2 text-foreground">
								What is the impact of this reflection?
							</label>
							<Select
								value={
									reflectionResponses[selectedReflection.id]?.impact !== null &&
									reflectionResponses[selectedReflection.id]?.impact !==
										undefined
										? reflectionResponses[
												selectedReflection.id
											].impact!.toString()
										: undefined
								}
								onValueChange={(v) =>
									setReflectionResponses((prev) => ({
										...prev,
										[selectedReflection.id]: {
											...(prev[selectedReflection.id] || {
												impact: null,
												reasons: "",
												signals: [],
											}),
											impact: v ? Number(v) : null,
										},
									}))
								}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select impact level" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="0">No Impact</SelectItem>
									<SelectSeparator />
									<SelectItem value="3">
										<span className="flex items-center">
											<span className="text-green-600 font-bold mr-1">+++</span>
											Break-through Positive
										</span>
									</SelectItem>
									<SelectItem value="2">
										<span className="flex items-center">
											<span className="text-green-600 font-bold mr-1">++</span>
											Highly Positive
										</span>
									</SelectItem>
									<SelectItem value="1">
										<span className="flex items-center">
											<span className="text-green-600 font-bold mr-1">+</span>
											Slightly Positive
										</span>
									</SelectItem>
									<SelectSeparator />
									<SelectItem value="-1">
										<span className="flex items-center">
											<span className="text-red-600 font-bold mr-1">-</span>
											Slightly Negative
										</span>
									</SelectItem>
									<SelectItem value="-2">
										<span className="flex items-center">
											<span className="text-red-600 font-bold mr-1">--</span>
											Highly Negative
										</span>
									</SelectItem>
									<SelectItem value="-3">
										<span className="flex items-center">
											<span className="text-red-600 font-bold mr-1">---</span>
											Critically Negative
										</span>
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<div>
							<label className="block text-sm font-semibold mb-2 text-foreground">
								Describe why the reflection is having this impact
							</label>
							<Textarea
								placeholder="Share your thoughts on how this reflection is affecting you today..."
								className="min-h-[80px] text-base leading-relaxed resize-y"
								value={
									reflectionResponses[selectedReflection.id]?.reasons || ""
								}
								onChange={(e) =>
									setReflectionResponses((prev) => ({
										...prev,
										[selectedReflection.id]: {
											...(prev[selectedReflection.id] || {
												impact: null,
												reasons: "",
												signals: [],
											}),
											reasons: e.target.value,
										},
									}))
								}
							/>
						</div>

						{selectedReflection.signals &&
							selectedReflection.signals.length > 0 && (
								<div className="pt-2">
									<label className="block text-sm font-semibold mb-3 text-foreground">
										Did you notice any of this reflection's signals today?
									</label>
									<div className="space-y-2.5">
										{selectedReflection.signals.map((signal) => (
											<div
												key={signal.id}
												className={`flex items-start space-x-3 p-2.5 rounded-lg border transition-all cursor-pointer ${
													reflectionResponses[
														selectedReflection.id
													]?.signals?.includes(signal.id)
														? "border-purple-300 bg-purple-50 dark:bg-purple-950/20"
														: "border-border hover:border-purple-200 hover:bg-purple-50/30"
												}`}
												onClick={() =>
													handleSignalToggle(
														selectedReflection.id,
														signal.id,
														!reflectionResponses[
															selectedReflection.id
														]?.signals?.includes(signal.id),
													)
												}
											>
												<input
													type="checkbox"
													id={`signal-${signal.id}`}
													className="mt-1 rounded border-2 border-gray-300 checked:bg-purple-600 checked:border-purple-600"
													checked={
														reflectionResponses[
															selectedReflection.id
														]?.signals?.includes(signal.id) || false
													}
													onChange={(e) =>
														handleSignalToggle(
															selectedReflection.id,
															signal.id,
															e.target.checked,
														)
													}
												/>
												<label
													htmlFor={`signal-${signal.id}`}
													className="text-sm flex-1 cursor-pointer"
												>
													<span className="font-medium text-foreground">
														{signal.title}
													</span>
													{signal.description && (
														<span className="text-muted-foreground block text-xs mt-1 leading-relaxed">
															{signal.description}
														</span>
													)}
												</label>
											</div>
										))}
									</div>
								</div>
							)}
					</div>
				</div>
			</div>
		</div>
	);
}
